import { useEffect, useState } from "react";
import { QrCode } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Modal } from "@/components/lims";

/**
 * The specimen QR, wherever a sample appears.
 *
 * The QR encodes the sample's detail URL, so scanning it opens that record.
 * It does not advance the workflow — the stage moves when the analyst writes
 * a result (`record_analyst_pickup` in `backend/server.py`), not when a code
 * is scanned.
 *
 * Two deliberate choices here:
 *
 * A row-sized QR is not scannable and pretending otherwise would be a lie.
 * A detail URL encodes to a 29- or 33-module symbol, so at the ~28px a table
 * row affords, each module lands under one pixel and no scanner will read it.
 * The row therefore carries a QR *glyph* — an affordance saying "a code lives
 * here" — and the real symbol appears large enough to scan when opened. That
 * also means the fetch happens on open rather than once per visible row, so a
 * 25-row table costs nothing until somebody wants a code.
 *
 * The image is fetched as a blob so an authorization or network failure can
 * render the explicit "unavailable" state instead of a browser broken-image
 * marker. Same-origin authentication is carried by the HttpOnly cookie.
 */
function useSampleQr(sampleId, enabled) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !sampleId) return undefined;
    let objectUrl = null;
    let live = true;
    setFailed(false);
    api
      .get(`/samples/${sampleId}/qr.svg`, { responseType: "blob" })
      .then(({ data: blob }) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    // Object URLs outlive the component unless revoked; without this, every
    // open would leak one.
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [sampleId, enabled]);

  return { url, failed };
}

function QrImage({ sampleId, sampleCode, enabled, className, testid }) {
  const { url, failed } = useSampleQr(sampleId, enabled);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center rounded border border-line bg-slate-50 p-2 text-center text-[10px] text-meta ${className}`}
        data-testid={testid ? `${testid}-failed` : undefined}
      >
        Code unavailable
      </div>
    );
  }

  return (
    <img
      src={url || undefined}
      alt={url ? `QR code linking to the record for ${sampleCode}` : ""}
      // White padding is part of the symbol as far as a scanner is concerned;
      // a QR flush against a border often will not read.
      className={`rounded border border-line bg-white p-1.5 ${className}`}
      data-testid={testid}
    />
  );
}

/** The enlarged, actually-scannable code. Shared by both entry points. */
function QrDialog({ sampleId, sampleCode, open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title={`Scan ${sampleCode}`} testid="sample-qr-modal">
      <div className="flex flex-col items-center gap-4">
        <QrImage
          sampleId={sampleId}
          sampleCode={sampleCode}
          enabled={open}
          className="h-64 w-64"
          testid="sample-qr-image"
        />
        <p className="text-center text-sm text-meta">
          Scanning opens this sample's record. It does not change the sample's
          stage.
        </p>
      </div>
    </Modal>
  );
}

/**
 * A QR glyph that opens the scannable code. For rows and dense headers.
 */
export default function SampleQrButton({ sampleId, sampleCode, className = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Show the scannable code for ${sampleCode}`}
        aria-label={`Show the scannable code for ${sampleCode}`}
        data-testid={`sample-qr-button-${sampleCode}`}
        className={`shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-ink ${className}`}
      >
        <QrCode size={16} />
      </button>
      <QrDialog
        sampleId={sampleId}
        sampleCode={sampleCode}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

/**
 * The code rendered in place, for panels with room for it.
 *
 * Where the surface is already about one specimen — a selected freezer
 * position, a sample header — the code is the point of being there, and
 * making somebody click to reveal it would be a step for nothing. Clicking
 * still enlarges it for a phone that will not focus this close.
 */
export function SampleQrInline({ sampleId, sampleCode, size = "h-24 w-24" }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Enlarge the code for ${sampleCode}`}
        aria-label={`Enlarge the code for ${sampleCode}`}
        data-testid={`sample-qr-inline-${sampleCode}`}
        className="shrink-0 rounded transition-opacity hover:opacity-80"
      >
        <QrImage
          sampleId={sampleId}
          sampleCode={sampleCode}
          enabled
          className={size}
          testid={`sample-qr-inline-image-${sampleCode}`}
        />
      </button>
      <QrDialog
        sampleId={sampleId}
        sampleCode={sampleCode}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
